import React, { useState, useEffect } from "react";
import "./Motibot.css";

const responses = {
  greeting: `👋 Hi there! I'm MotiBot, your delivery assistant. :


What can I help you with today?`,

  // Delivery types
  furniture: `Yes! We specialize in oversized items like:
- Sofas & sectionals
- Mattresses & bed frames
- Dining tables
- Entertainment units
- And all other furniture

We use professional equipment and padding to ensure safe delivery. Need a quote? <a href="/booking" style="color: #2d5d7c; font-weight: bold;">Schedule Your Delivery</a>`,

  appliances: `Absolutely! We deliver all appliances including:
- Refrigerators
- Washers & dryers
- Dishwashers
- Ovens & ranges
- HVAC equipment

Please note, We do not install appliances. <a href="/booking" style="color: #2d5d7c; font-weight: bold;">Book appliance delivery</a>`,

  // Service areas
  areas: `📍 We serve all of Edmonton and surrounding areas including:
- St. Albert
- Sherwood Park
- Beaumont
- Leduc
- Spruce Grove
- Stony Plain

Enter your address during booking to confirm coverage in your area!`,

  // Pricing
  pricing: `💰 Delivery pricing depends on:
1. Item size/type (oversized items may cost more)
2. Delivery urgency
3. Distance

💰 Payment options:
- One-time customers: Pay online during booking
- Business clients: Select 'Bill to Account' for invoices

<a href="/booking" style="color: #2d5d7c; font-weight: bold;">Get an instant quote</a>`,

  // Booking
  booking: `📅 How to book:
1. Click <a href="/booking" style="color: #2d5d7c; font-weight: bold;">Schedule Delivery</a>
2. Choose service type:
   - 🪑 Furniture
   - 🏷️ Retail parcels
   - 🏢 Business logistics
3. Select time window
4. Confirm details

No account needed! Takes <2 minutes ⏱️`,

  // Tracking
  tracking: `📦 Track your delivery:
1. Use our <a href="/track-delivery" style="color: #2d5d7c; font-weight: bold;">Track My Delivery</a> feature
2. Enter your tracking number
3. View real-time GPS updates

We provide:
- Live driver location
- Estimated arrival time
- Delivery confirmation with photos`,

  // Business services
  business: `🏢 Business Solutions:
- Scheduled B2B deliveries
- Retail store replenishment
- Dedicated account managers
- Invoice billing
- Volume discounts

<a href="/contact" style="color: #2d5d7c; font-weight: bold;">Contact our business team</a> for custom solutions!`,

  // Urgent delivery
  urgent: `⚡ Urgent Delivery Options:
- Same-day (if booked before 12pm)
- Next-day guaranteed
- Emergency rush service (extra fee)

Availability depends on route space. <a href="/booking" style="color: #2d5d7c; font-weight: bold;">Book now</a> or call 780-901-3330 for urgent requests!`,

  // Support
  support: `📞 Need human help?
  
Email: <a href="mailto:support@motitransport.com" style="color: #2d5d7c; font-weight: bold;">support@motitransport.com</a>
Phone: <a href="tel:7809013330" style="color: #2d5d7c; font-weight: bold;">780-901-3330</a>
Hours: Mon-Fri 8AM-6PM

For fastest help with active deliveries, include your tracking #!`,

  default: `I'm not sure I understand. Here are some things I can help with:
- Delivery pricing and services
- Booking and tracking
- Service areas
- Business delivery options

Try asking about one of these, or contact <a href="mailto:support@motitransport.com" style="color: #2d5d7c; font-weight: bold;">support@motitransport.com</a> for specific questions!`,
};

const Motibot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [showQuickRepliesAfterGreeting, setShowQuickRepliesAfterGreeting] =
    useState(false);

  // Enhanced responses with better formatting and links

  // Enhanced quick replies with emojis
  const quickReplies = [
    { label: "🛋️ Furniture delivery", key: "furniture" },
    { label: "🧺 Appliance delivery", key: "appliances" },
    { label: "🚚 Track delivery", key: "tracking" },
    { label: "💰 Get pricing", key: "pricing" },
    { label: "📅 Book delivery", key: "booking" },
    { label: "📍 Service areas", key: "areas" },
    { label: "🏢 Business solutions", key: "business" },
    { label: "⏱️ Urgent delivery", key: "urgent" },
  ];

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addBotMessage(responses.greeting);
      setShowQuickRepliesAfterGreeting(true);
    }
  }, [isOpen, messages.length]);

  const addBotMessage = (text) => {
    setIsBotTyping(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { text, isUser: false }]);
      setIsBotTyping(false);
    }, 800);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage = { text: inputValue, isUser: true };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setShowQuickRepliesAfterGreeting(false); // Hide quick replies after user sends a message

    // Process message and generate response
    setTimeout(() => {
      const lowerInput = inputValue.toLowerCase();
      let responseKey = "default";

      // Enhanced keyword detection
      if (/(track|status|where is)/i.test(lowerInput)) responseKey = "tracking";
      else if (/(book|schedule|reserve)/i.test(lowerInput))
        responseKey = "booking";
      else if (
        /(area|location|city|deliver|delivery|edmonton|sherwood|st\.? albert|leduc)/i.test(
          lowerInput
        )
      )
        responseKey = "areas";
      else if (/(price|cost|how much)/i.test(lowerInput))
        responseKey = "pricing";
      else if (/(bill|invoice|account)/i.test(lowerInput))
        responseKey = "business";
      else if (/(support|help|contact)/i.test(lowerInput))
        responseKey = "support";
      else if (/(urgent|rush|fast|quick|asap)/i.test(lowerInput))
        responseKey = "urgent";
      else if (/(furniture|sofa|couch|table|mattress|bed)/i.test(lowerInput))
        responseKey = "furniture";
      else if (/(appliance|fridge|washer|dryer|stove|oven)/i.test(lowerInput))
        responseKey = "appliances";
      else if (/(business|b2b|commercial|retail|store)/i.test(lowerInput))
        responseKey = "business";

      addBotMessage(responses[responseKey]);
    }, 500);
  };

  const handleQuickReply = (key) => {
    setMessages((prev) => [
      ...prev,
      { text: quickReplies.find((r) => r.key === key).label, isUser: true },
    ]);
    setShowQuickRepliesAfterGreeting(false);
    setTimeout(() => {
      addBotMessage(responses[key]);
    }, 500);
  };

  // Function to create HTML from text with links
  const createMarkup = (html) => {
    return { __html: html };
  };

  return (
    <>
      {/* Floating chat button */}
      {!isOpen && (
        <button
          className="motibot-floating-btn"
          onClick={() => setIsOpen(true)}
          aria-label="Open MotiBot chat"
        >
          <span className="motibot-icon">🚚</span>
          <span className="motibot-pulse"></span>
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className="motibot-chat-window">
          <div className="motibot-header">
            <h3>MotiBot Assistant</h3>
            <button
              className="motibot-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close MotiBot chat"
            >
              ×
            </button>
          </div>

          <div className="motibot-messages">
            {messages.map((msg, index) => (
              <React.Fragment key={index}>
                <div
                  className={`motibot-message ${msg.isUser ? "user" : "bot"}`}
                  dangerouslySetInnerHTML={
                    !msg.isUser ? createMarkup(msg.text) : { __html: msg.text }
                  }
                />
                {/* Show quick replies only after the greeting message */}
                {!msg.isUser &&
                  msg.text === responses.greeting &&
                  showQuickRepliesAfterGreeting && (
                    <div className="motibot-quick-replies-container">
                      <div className="motibot-quick-replies">
                        {quickReplies.map((reply, index) => (
                          <button
                            key={index}
                            className="motibot-quick-reply"
                            onClick={() => handleQuickReply(reply.key)}
                          >
                            {reply.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
              </React.Fragment>
            ))}
            {isBotTyping && (
              <div className="motibot-message bot">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
          </div>

          <div className="motibot-input-area">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your question..."
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            />
            <button
              className="motibot-send-btn"
              onClick={handleSendMessage}
              disabled={!inputValue.trim()}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Motibot;
